import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IDefaultFailureType } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable, FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { IFailureCode } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { UserContext } from "contexts/UserContext/UserContext";
import { CircularProgress } from "@mui/material";
import { EventReportsEntity } from "entities/EventReports/EventReportsEntity";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { DropdownOptions, IOption } from "../Dropdown";

export const FailureCodeDetailsDropdown = ({
    defaultFailureTypes,
    row,
}: {
    defaultFailureTypes: IDefaultFailureType[];
    row: IEventReportDetails;
}) => {
    const { i18n } = useTranslation();
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFunctionalLocationId = useMemo(
        () => edit[existingEdition]?.changes?.functional_location_id,
        [edit, existingEdition],
    );
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedFailureTypeId = useMemo(
        () => (selectedFailureTypeIdIsNotSelected ? undefined : edit[existingEdition]?.changes?.failure_type_id),
        [existingEdition, edit, selectedFailureTypeIdIsNotSelected],
    );
    const selectedFailureCodeId = useMemo(
        () => edit[existingEdition]?.changes?.failure_code_id,
        [edit, existingEdition],
    );

    const { loading, locationSpecificFailureCodes, nonLocationSpecificFailureCodes } =
        EventReportsEntity.GetLocationSpecificFailureCodesForSecondaryCodesEdition(
            row.machine_center.machine_center_id,
            selectedFunctionalLocationId,
        );

    const nonLocationSpecificOptions = useMemo(
        () =>
            nonLocationSpecificFailureCodes.map((failureCode) => ({
                codeId: failureCode.failure_code.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
                typeId: failureCode.failure_code.failure_type_id,
            })) || [],
        [nonLocationSpecificFailureCodes, i18n],
    );

    const locationSpecificOptions = useMemo(
        () =>
            locationSpecificFailureCodes.map((failureCode) => ({
                codeId: failureCode.failure_code.failure_code_id,
                name: translateDefaultEntity(
                    i18n.language,
                    failureCode.failure_code as unknown as IFailureCode,
                    "name",
                    DefaultTranslationFieldsInTable.FAILURE_CODE,
                ),
                typeId: failureCode.failure_code.failure_type_id,
            })),
        [locationSpecificFailureCodes, i18n],
    );

    const defaultOptions = useMemo(
        () =>
            defaultFailureTypes.reduce(
                (
                    accumulator: {
                        codeId: number;
                        name: string;
                        typeId: number;
                    }[],
                    failureType,
                ) => {
                    let failureCodesAccumulated = accumulator;
                    failureCodesAccumulated = [
                        ...failureCodesAccumulated,
                        ...failureType.failure_codes.map((failureCode) => ({
                            codeId: failureCode.failure_code_id,
                            name: translateDefaultEntity(
                                i18n.language,
                                failureCode as unknown as IFailureCode,
                                "name",
                                DefaultTranslationFieldsInTable.FAILURE_CODE,
                            ),
                            typeId: failureType.failure_type_id,
                        })),
                    ];

                    return failureCodesAccumulated;
                },
                [],
            ),
        [defaultFailureTypes, i18n],
    );

    const allFailureCodeOptions = useMemo(
        () => [...nonLocationSpecificOptions, ...locationSpecificOptions, ...defaultOptions],
        [locationSpecificOptions, defaultOptions, nonLocationSpecificOptions],
    );

    const failureCodesOptionsBasedOnSelection = useMemo(
        () =>
            allFailureCodeOptions.reduce((accumulator: IOption[], failureCodeOption) => {
                let failureCodeOptionsAcc = accumulator;
                if (selectedFailureTypeId === failureCodeOption.typeId) {
                    if (!failureCodeOptionsAcc.find((failureCodeAcc) => failureCodeOption.codeId === failureCodeAcc.id))
                        failureCodeOptionsAcc = [
                            ...failureCodeOptionsAcc,
                            { id: failureCodeOption.codeId, name: failureCodeOption.name },
                        ];
                }
                return failureCodeOptionsAcc;
            }, []),
        [allFailureCodeOptions, selectedFailureTypeId],
    );

    const currentSelectionNotInOptions = useMemo(
        () => failureCodesOptionsBasedOnSelection.some((option) => option.id === selectedFailureCodeId),
        [selectedFailureCodeId, failureCodesOptionsBasedOnSelection],
    );

    const handleFailureCodeUpdate = async (id: number) => {
        if (existingEdition !== -1) {
            setEdit((prevEdit) => {
                const newEdit = [...prevEdit];
                newEdit[existingEdition] = {
                    ...newEdit[existingEdition],
                    changes: {
                        ...newEdit[existingEdition]?.changes,
                        failure_code_id: id,
                        ...(user_id && { edited_by_user_id: user_id }),
                    },
                };
                return newEdit;
            });
        }
    };

    return loading ? (
        <CircularProgress size={20} color="info" />
    ) : (
        <DropdownOptions
            disabled={
                !selectedFailureTypeId || (edit[existingEdition]?.location_specific && !selectedFunctionalLocationId)
            }
            obligatory={!selectedFailureTypeIdIsNotSelected}
            inlineStyle={{ width: "80px" }}
            pending={!selectedFailureCodeId || !currentSelectionNotInOptions}
            optionSelected={selectedFailureCodeId}
            options={failureCodesOptionsBasedOnSelection}
            handleUpdate={handleFailureCodeUpdate}
        />
    );
};
